import { AddCircleOutline, Check, Close, ExpandLess, ExpandMore, History, Search } from '@mui/icons-material'
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Collapse,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  styled,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { FC, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { invalidationActivityLogCache } from 'src/api/activity-log'
import { invalidateLocalityCache } from 'src/api/localities'
import { useGetActiveEquipmentForLocality } from 'src/api/main-component'
import { invalidateMaintenanceCache } from 'src/api/maintenance'
import {
  invalidateScheduledMaintenanceCache,
  useGetEquipmentPerformedMaintenances,
  useGetLocalityPerformedMaintenances,
  useGetMaintenanceList,
  usePerformScheduledMaintenances,
} from 'src/api/scheduled-maintenance'
import { AppRoutesNavigation } from 'src/app-routes'
import { CommonPageLayout } from 'src/components/common/CommonPageLayout'
import { EmptyStatusCard } from 'src/components/mooring/EmptyStatusCard'
import { FlexSpinner } from 'src/components/Spinner'
import { useNavigation } from 'src/context/NavigationContext'
import { DialogContextType, RepeatType } from 'src/enums'
import { IDeviationType } from 'src/generated-types/deviation-type'
import { IMaintenanceListItem } from 'src/generated-types/scheduled-maintenance'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { MUIThemeColors } from 'src/mui-theme'
import { compareByReference, extractPlainText, getFormattedDate, getThemeColor } from 'src/utils'
import { getCageDisplayLabel, getCageGenericIdentification, getMooringName, getUTCDate } from 'src/utils/function'
import { getTranslatedText } from 'src/utils/translations'
import { ReactComponent as MaintenanceHeaderIcon } from '../../../assets/svg/maintenance.svg'
import NoMaintenance from '../../../assets/svg/no-maintenances.svg'
import { CreatePositionDeviationDialog } from '../deviations/CreatePositionDeviationDialog'
import BatchActionBar from './BatchActionBar'
import { CreateCustomMaintenanceDialog } from './CreateCustomMaintenanceDialog'
import { StyledPerformedButton } from './LocalityMaintenanceContainer'
import { MaintenanceListPerformDialog } from './MaintenanceListPerformDialog'
import MaintenanceListRow from './MaintenanceListRow'
import { ScheduledMaintenanceSkipDialog } from './ScheduledMaintenanceSkipDialog'

type TabValue = 'today' | 'overdue' | 'upcoming' | 'done'
type GroupBy = 'none' | 'cage' | 'equipment' | 'date-frequency'

const REPEAT_TYPE_LABEL: Record<number, string> = {
  [RepeatType.Never]: 'never',
  [RepeatType.Daily]: 'daily',
  [RepeatType.Weekly]: 'weekly',
  [RepeatType.Monthly]: 'monthly',
  [RepeatType.Yearly]: 'yearly',
}

const formatDateLabel = (dateStr: string, t: (key: string, opts?: any) => string): string => {
  const date = new Date(dateStr)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const target = new Date(date)
  target.setHours(0, 0, 0, 0)

  const diffDays = Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return `${t('today', { ns: 'common' })} (${getFormattedDate(date)})`
  if (diffDays === -1) return `${t('yesterday', { ns: 'common' })} (${getFormattedDate(date)})`
  if (diffDays < -1) return `${getFormattedDate(date)} (${Math.abs(diffDays)} ${t('days-ago', { ns: 'maintenance' })})`
  if (diffDays === 1) return `${getFormattedDate(date)} (${t('tomorrow', { ns: 'common' })})`
  return `${getFormattedDate(date)} (${t('in-days', { ns: 'maintenance', count: diffDays })})`
}

interface MaintenanceListViewProps {
  isDialog?: boolean
  pop?: () => void
}

const MaintenanceListView: FC<MaintenanceListViewProps> = ({ isDialog = false, pop }) => {
  const theme = useTheme()
  const { t } = useTranslation(['maintenance', 'common'])
  const { customerId, localityId } = useParams()
  const { navigateTo } = useNavigation()
  const { showDialog, closeAllDialogs } = useDialogCtx()
  const isTabletScreen = useMediaQuery('(max-width:1630px)')

  const [activeTab, setActiveTab] = useState<TabValue>('today')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [groupBy, setGroupBy] = useState<GroupBy>('date-frequency')
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({})
  const [performingRowId, setPerformingRowId] = useState<number | null>(null)
  const [isBatchPerforming, setIsBatchPerforming] = useState(false)

  const { data: maintenanceList, isLoading } = useGetMaintenanceList(customerId as EntityId, localityId as EntityId)

  const { data: activeEquipment } = useGetActiveEquipmentForLocality(customerId as EntityId, localityId as EntityId)
  const { data: equipmentPerformedHistory } = useGetEquipmentPerformedMaintenances(
    customerId as EntityId,
    localityId as EntityId,
    { enabled: isDialog },
  )
  const { data: localityPerformedHistory } = useGetLocalityPerformedMaintenances(
    customerId as EntityId,
    localityId as EntityId,
    { enabled: isDialog },
  )
  const performMutation = usePerformScheduledMaintenances(customerId as EntityId, localityId as EntityId)
  const historyCount = (equipmentPerformedHistory?.length ?? 0) + (localityPerformedHistory?.length ?? 0)

  // Filter items by tab and search
  const filteredItems = useMemo(() => {
    if (!maintenanceList) return []

    let items = maintenanceList

    // Filter by search
    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      items = items.filter(
        item =>
          item.maintenanceName.toLowerCase().includes(q) ||
          getTranslatedText(item.mainComponentTypeName)?.toLowerCase().includes(q) ||
          item.mainComponentName?.toLowerCase().includes(q) ||
          item.serialNumber?.toLowerCase().includes(q) ||
          item.positionReference?.toLowerCase().includes(q) ||
          item.positionNote?.toLowerCase().includes(q) ||
          item.mainComponentReference?.toLowerCase().includes(q) ||
          (item.positionName && getCageGenericIdentification(item.positionName)?.toLowerCase().includes(q)) ||
          (item.positionName &&
            getCageDisplayLabel(item.positionName, item.positionReference).toLowerCase().includes(q)) ||
          (item.mooringName && getMooringName(item.mooringName).toLowerCase().includes(q)) ||
          item.mooringName?.toLowerCase().includes(q) ||
          item.tags?.some(tag => tag.toLowerCase().includes(q)),
      )
    }

    // Filter by tab
    switch (activeTab) {
      case 'overdue':
        items = items.filter(item => item.isOverdue)
        break
      case 'today':
        items = items.filter(item => item.isDueToday)
        break
      case 'upcoming':
        items = items.filter(item => !item.isOverdue && !item.isDueToday)
        break
    }

    return items
  }, [maintenanceList, activeTab, searchQuery])

  const tabFilteredItems = useMemo(() => {
    if (!maintenanceList) return []
    switch (activeTab) {
      case 'overdue':
        return maintenanceList.filter(item => item.isOverdue)
      case 'today':
        return maintenanceList.filter(item => item.isDueToday)
      case 'upcoming':
        return maintenanceList.filter(item => !item.isOverdue && !item.isDueToday)
      default:
        return maintenanceList
    }
  }, [maintenanceList, activeTab])

  // Two-level section type for date-frequency grouping
  type SubSection = { title: string; items: IMaintenanceListItem[] }
  type Section = {
    title: string
    items: IMaintenanceListItem[]
    color?: string
    subSections?: SubSection[]
    sectionKey?: string
  }

  const toggleSectionExpanded = useCallback((sectionKey: string) => {
    setExpandedSections(prev => ({ ...prev, [sectionKey]: !prev[sectionKey] }))
  }, [])

  // Group items by section for display
  const sections = useMemo((): Section[] => {
    if (groupBy === 'date-frequency') {
      // Group by date
      const byDate = new Map<string, IMaintenanceListItem[]>()
      for (const item of filteredItems) {
        const dateKey = item.scheduledDate?.split('T')[0] || 'unknown'
        if (!byDate.has(dateKey)) byDate.set(dateKey, [])
        byDate.get(dateKey)!.push(item)
      }

      const sortedDates = Array.from(byDate.keys()).sort()

      return sortedDates.map(dateKey => {
        const dateItems = byDate.get(dateKey)!
        const dateLabel = formatDateLabel(dateKey, t)
        const hasOverdue = dateItems.some(i => i.isOverdue)
        const allToday = dateItems.every(i => i.isDueToday)
        const dateColor = hasOverdue
          ? theme.palette.error.main
          : allToday
          ? theme.palette.primary.main
          : theme.palette.text.secondary

        // Sub-group by frequency
        const byFrequency = new Map<number, IMaintenanceListItem[]>()
        for (const item of dateItems) {
          if (!byFrequency.has(item.repeatType)) byFrequency.set(item.repeatType, [])
          byFrequency.get(item.repeatType)!.push(item)
        }

        const subSections: SubSection[] = Array.from(byFrequency.entries()).map(([repeatType, freqItems]) => ({
          title: t(REPEAT_TYPE_LABEL[repeatType] || 'never', { ns: 'maintenance' }),
          items: freqItems,
        }))

        return {
          title: dateLabel,
          items: dateItems,
          color: dateColor,
          subSections,
        }
      })
    }

    if (groupBy === 'cage' || groupBy === 'equipment') {
      const grouped = new Map<string, { items: IMaintenanceListItem[]; sortKey: string }>()
      for (const item of filteredItems) {
        let key: string
        let sortKey: string
        if (groupBy === 'cage') {
          if (item.isLocalityTask) {
            key = t('locality', { ns: 'maintenance' })
            sortKey = key
          } else if (item.positionName) {
            const cageLabel = getCageDisplayLabel(item.positionName, item.positionReference)
            key = `${cageLabel}${item.mooringName ? ` | ${getMooringName(item.mooringName)}` : ''}`
            sortKey = cageLabel
          } else {
            key = t('unassigned', { ns: 'common' })
            sortKey = key
          }
        } else {
          key = item.isLocalityTask
            ? t('locality', { ns: 'maintenance' })
            : getTranslatedText(item.mainComponentTypeName) || t('unassigned', { ns: 'common' })
          sortKey = key
        }
        if (!grouped.has(key)) grouped.set(key, { items: [], sortKey })
        grouped.get(key)!.items.push(item)
      }
      const entries = Array.from(grouped.entries()).map(([title, { items, sortKey }]) => ({ title, items, sortKey }))
      if (groupBy === 'cage') {
        entries.sort((a, b) => compareByReference(a.sortKey, b.sortKey))
      }
      return entries.map(({ title, items }) => ({ title, items }))
    }

    if (groupBy === 'none') {
      const overdue = filteredItems.filter(i => i.isOverdue)
      const today = filteredItems.filter(i => i.isDueToday && !i.isOverdue)
      const upcoming = filteredItems.filter(i => !i.isOverdue && !i.isDueToday)
      const result: Section[] = []
      if (overdue.length > 0) {
        result.push({
          title: t('overdue', { ns: 'maintenance' }),
          items: overdue,
          color: theme.palette.error.main,
          sectionKey: 'overdue',
        })
      }
      if (today.length > 0) {
        result.push({
          title: t('due-today', { ns: 'maintenance' }),
          items: today,
          color: theme.palette.primary.main,
          sectionKey: 'due-today',
        })
      }
      if (upcoming.length > 0) {
        result.push({
          title: t('upcoming', { ns: 'maintenance' }),
          items: upcoming,
          color: theme.palette.text.secondary,
          sectionKey: 'upcoming',
        })
      }
      return result
    }

    return []
  }, [filteredItems, groupBy, t, theme])

  // Counts for tabs
  const overdueCount = maintenanceList?.filter(i => i.isOverdue).length ?? 0
  const todayCount = maintenanceList?.filter(i => i.isDueToday).length ?? 0
  const upcomingCount = maintenanceList?.filter(i => !i.isOverdue && !i.isDueToday).length ?? 0

  const invalidateAll = useCallback(() => {
    invalidateScheduledMaintenanceCache.invalidateAllScheduledMaintenances(
      customerId as EntityId,
      localityId as EntityId,
    )
    invalidateScheduledMaintenanceCache.getLocalityScheduledMaintenances(customerId as EntityId, localityId as EntityId)
    invalidateScheduledMaintenanceCache.getMaintenanceList(customerId as EntityId, localityId as EntityId)
    invalidateLocalityCache.getMaintenanceTypesCount(customerId as EntityId, localityId as EntityId)
    invalidateMaintenanceCache.invalidateAllMaintenances(customerId as EntityId, localityId as EntityId)
    invalidateScheduledMaintenanceCache.getEquipmentPerformedMaintenances(
      customerId as EntityId,
      localityId as EntityId,
    )
    invalidateScheduledMaintenanceCache.getLocalityPerformedMaintenances(customerId as EntityId, localityId as EntityId)
    invalidationActivityLogCache.getCageActivityLogs()
  }, [customerId, localityId])

  // Quick perform (one-tap done)
  const handleQuickPerform = useCallback(
    async (item: IMaintenanceListItem) => {
      if (performingRowId !== null) return

      setPerformingRowId(item.id)
      try {
        await performMutation.mutateAsync({
          scheduledMaintenanceIds: [item.id],
          note: '',
          performedDate: getUTCDate(new Date()),
          isSinglePerform: true,
        })
        invalidateAll()
        toast.success(`${item.maintenanceName} — ${t('performed-successfully', { ns: 'maintenance' })}`)
      } catch {
        toast.error(t('unExpectedError', { ns: 'common-api-message' }))
      } finally {
        setPerformingRowId(null)
      }
    },
    [performMutation, invalidateAll, performingRowId, t],
  )

  // Batch perform
  const handleBatchPerform = useCallback(async () => {
    if (selectedIds.size === 0 || isBatchPerforming || performingRowId !== null) return
    const performableIds = filteredItems
      .filter(item => selectedIds.has(item.id) && item.isPerformable)
      .map(item => item.id)
    if (performableIds.length === 0) {
      toast.warning(t('no-performable-selected', { ns: 'maintenance' }))
      return
    }
    setIsBatchPerforming(true)
    try {
      await performMutation.mutateAsync({
        scheduledMaintenanceIds: performableIds,
        note: '',
        performedDate: getUTCDate(new Date()),
      })
      invalidateAll()
      setSelectedIds(new Set())
      toast.success(`${performableIds.length} ${t('maintenances-were-performed', { ns: 'maintenance' })}`)
    } catch {
      toast.error(t('unExpectedError', { ns: 'common-api-message' }))
    } finally {
      setIsBatchPerforming(false)
    }
  }, [selectedIds, filteredItems, performMutation, invalidateAll, isBatchPerforming, performingRowId, t])

  // Single skip via dialog
  const handleSkip = useCallback(
    (item: IMaintenanceListItem) => {
      showDialog(
        ScheduledMaintenanceSkipDialog,
        {
          title: t('are-you-sure-to-skip', { ns: 'maintenance' }),
          componentProps: {
            customerId: customerId as EntityId,
            localityId: localityId as EntityId,
            scheduledMaintenance: {
              id: item.id,
              maintenance: { name: item.maintenanceName, note: item.maintenanceNote },
            } as any,
            onlyDaily: false,
          },
          opts: { maxWidth: 'sm', fullWidth: true },
        },
        undefined,
        false,
      )
    },
    [showDialog, customerId, localityId],
  )

  // Perform with note via existing dialog
  const handlePerformWithNote = useCallback(
    (item: IMaintenanceListItem) => {
      showDialog(
        MaintenanceListPerformDialog,
        {
          title: t('are-you-sure-to-perform', { ns: 'maintenance' }),
          componentProps: {
            customerId: customerId as EntityId,
            localityId: localityId as EntityId,
            item,
          },
          opts: { maxWidth: 'sm', fullWidth: true },
        },
        undefined,
        false,
      )
    },
    [showDialog, customerId, localityId],
  )

  const handleCreateDeviation = useCallback(
    (item: IMaintenanceListItem) => {
      if (item.mainComponentId == null) return

      const identification = [
        item.mainComponentTypeName ? getTranslatedText(item.mainComponentTypeName) : undefined,
        item.serialNumber,
        item.positionName ? getCageDisplayLabel(item.positionName, item.positionReference) : undefined,
      ]
        .filter(Boolean)
        .join(' | ')

      const noteText = item.maintenanceNote ? extractPlainText(item.maintenanceNote).trim() : ''
      const descriptionParts = [
        t('deviation-from-maintenance-context', {
          ns: 'maintenance',
          name: item.maintenanceName,
          date: item.scheduledDate ? new Date(item.scheduledDate).toLocaleDateString() : '',
        }),
        identification ? `${t('equipment', { ns: 'maintenance' })}: ${identification}` : undefined,
        noteText || undefined,
      ].filter(Boolean)

      showDialog(
        CreatePositionDeviationDialog,
        {
          componentProps: {
            customerId: customerId as EntityId,
            localityId: localityId as EntityId,
            mainComponent: {
              id: item.mainComponentId,
              serialNumber: item.serialNumber,
              name: item.mainComponentName,
              mainComponentTypeName: item.mainComponentTypeName,
              type: {
                name: item.mainComponentTypeName ?? '',
              },
            } as any,
            maintenanceId: item.maintenanceId,
            type: IDeviationType.Maintenance,
            deviationTypeFromParent: IDeviationType.Maintenance,
            disableSteps: true,
            initialValues: {
              title: item.maintenanceName,
              description: descriptionParts.join('\n'),
            },
            showExistingDeviations: true,
          },
          opts: { maxWidth: 'md', fullWidth: true },
        },
        DialogContextType.localityContext,
        true,
      )
    },
    [showDialog, customerId, localityId, t],
  )

  const toggleSelect = useCallback((id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const toggleSelectAll = useCallback(() => {
    const allIds = filteredItems.map(i => i.id)
    const allSelected = allIds.every(id => selectedIds.has(id))
    if (allSelected) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(allIds))
    }
  }, [filteredItems, selectedIds])

  const onAdd = useCallback(() => {
    showDialog(
      CreateCustomMaintenanceDialog,
      {
        componentProps: {
          customerId: customerId as EntityId,
          localityId: localityId as EntityId,
          mainComponents: activeEquipment || [],
          isLocalityMaintenance: false,
        },
        opts: { maxWidth: 'sm', fullWidth: true },
      },
      undefined,
      true,
    )
  }, [showDialog, customerId, localityId, activeEquipment])

  const allVisibleSelected = filteredItems.length > 0 && filteredItems.every(i => selectedIds.has(i.id))

  const handleHistoryClick = useCallback(() => {
    navigateTo(
      AppRoutesNavigation.Localities.equipmentPerformedMaintenances(customerId as EntityId, localityId as EntityId),
    )
    closeAllDialogs()
  }, [navigateTo, customerId, localityId, closeAllDialogs])

  const renderTabLabel = (
    label: string,
    count: number,
    options?: { badgeVariant?: 'default' | 'error'; labelColor?: string },
  ) => {
    const badgeVariant = options?.badgeVariant ?? 'default'
    const useErrorBadge = badgeVariant === 'error' && count > 0

    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
        <Box
          component="span"
          sx={{
            fontWeight: 600,
            fontSize: 'inherit',
            ...(options?.labelColor ? { color: options.labelColor } : {}),
          }}
        >
          {label}
        </Box>
        <Box
          component="span"
          sx={{
            fontSize: '0.6875rem',
            fontWeight: 700,
            lineHeight: '18px',
            borderRadius: '10px',
            px: 0.75,
            minWidth: 18,
            textAlign: 'center',
            ...(useErrorBadge
              ? {
                  backgroundColor: theme.palette.error.main,
                  color: theme.palette.common.white,
                }
              : {
                  backgroundColor: theme.palette.action.selected,
                  color: theme.palette.text.secondary,
                }),
          }}
        >
          {count}
        </Box>
      </Box>
    )
  }

  const renderTabs = (modern = false) => (
    <Tabs
      value={activeTab}
      onChange={(_, v) => {
        setActiveTab(v)
        setSelectedIds(new Set())
      }}
      textColor="inherit"
      indicatorColor="primary"
      variant={modern ? 'standard' : 'fullWidth'}
      sx={{
        mb: modern ? 0 : 1,
        minHeight: 36,
        flex: modern ? 1 : undefined,
        '& .MuiTab-root': {
          color: theme.palette.text.primary,
          opacity: 0.7,
          minHeight: 36,
          py: 0.5,
          px: modern ? 2 : 1,
          fontSize: { xs: '0.8rem', sm: '0.875rem' },
          textTransform: 'none',
          fontWeight: 600,
          '&.Mui-selected': {
            color: theme.palette.primary.main,
            opacity: 1,
          },
        },
      }}
    >
      <Tab value="today" label={renderTabLabel(t('today', { ns: 'common' }), todayCount)} />
      <Tab
        value="overdue"
        label={renderTabLabel(t('overdue', { ns: 'maintenance' }), overdueCount, {
          badgeVariant: 'error',
          labelColor: theme.palette.error.main,
        })}
      />
      <Tab value="upcoming" label={renderTabLabel(t('upcoming', { ns: 'maintenance' }), upcomingCount)} />
    </Tabs>
  )

  const renderSearchAndGroup = (fullWidthSearch = false) => (
    <Box
      sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, mb: 1, alignItems: { sm: 'center' } }}
    >
      <TextField
        size="small"
        placeholder={`${t('search', { ns: 'common' })}...`}
        value={searchQuery}
        onChange={e => setSearchQuery(e.target.value)}
        sx={{ flex: 1, maxWidth: fullWidthSearch ? undefined : { sm: 400 } }}
        fullWidth={fullWidthSearch}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search fontSize="small" />
            </InputAdornment>
          ),
        }}
      />
      <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 } }}>
        <InputLabel>{t('group-by', { ns: 'maintenance' })}</InputLabel>
        <Select
          value={groupBy}
          label={t('group-by', { ns: 'maintenance' })}
          onChange={e => setGroupBy(e.target.value as GroupBy)}
        >
          <MenuItem value="none">{t('none', { ns: 'common' })}</MenuItem>
          <MenuItem value="cage">{t('cage', { ns: 'common' })}</MenuItem>
          <MenuItem value="equipment">{t('equipment', { ns: 'common' })}</MenuItem>
          <MenuItem value="date-frequency">{t('date-frequency', { ns: 'maintenance' })}</MenuItem>
        </Select>
      </FormControl>
    </Box>
  )

  const renderListContent = () => {
    if (isLoading) return <FlexSpinner />

    if (filteredItems.length === 0) {
      return (
        <Box className="text-center" sx={{ py: 4 }}>
          <EmptyStatusCard
            image={NoMaintenance}
            header={t('no-maintenance-title', { ns: 'maintenance' })}
            content={t('no-maintenance-content', { ns: 'maintenance' })}
            imageWidth="60px"
            headerFontSize="18px"
            contentFontSize="14px"
            contentTopMargin="0px"
            compact={true}
          />
        </Box>
      )
    }

    return sections.map((section, sectionIdx) => {
      const isCollapsibleSection = isDialog && groupBy === 'none'
      const sectionKey = section.sectionKey ?? String(sectionIdx)
      const isExpanded = expandedSections[sectionKey] ?? true

      const rows = section.subSections
        ? section.subSections.map((sub, subIdx) => (
            <Box key={subIdx}>
              <SubSectionHeader>
                <Typography variant="caption" fontWeight={600} color="text.secondary">
                  {sub.title}
                </Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  {t('tasks-count', { ns: 'maintenance', count: sub.items.length })}
                </Typography>
              </SubSectionHeader>
              {sub.items.map(item => (
                <MaintenanceListRow
                  key={item.id}
                  item={item}
                  isSelected={selectedIds.has(item.id)}
                  onToggleSelect={toggleSelect}
                  onQuickPerform={handleQuickPerform}
                  onSkip={handleSkip}
                  onPerformWithNote={handlePerformWithNote}
                  onCreateDeviation={handleCreateDeviation}
                  performingRowId={performingRowId}
                  isModernLayout={isDialog}
                />
              ))}
            </Box>
          ))
        : section.items.map(item => (
            <MaintenanceListRow
              key={item.id}
              item={item}
              isSelected={selectedIds.has(item.id)}
              onToggleSelect={toggleSelect}
              onQuickPerform={handleQuickPerform}
              onSkip={handleSkip}
              onPerformWithNote={handlePerformWithNote}
              onCreateDeviation={handleCreateDeviation}
              performingRowId={performingRowId}
              isModernLayout={isDialog}
            />
          ))

      if (isCollapsibleSection) {
        return (
          <Box key={sectionIdx}>
            <CollapsibleSectionHeader onClick={() => toggleSectionExpanded(sectionKey)}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                <Typography variant="subtitle2" fontWeight={700} color={section.color || 'text.primary'}>
                  {section.title}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                {t('tasks-count', { ns: 'maintenance', count: section.items.length })}
              </Typography>
            </CollapsibleSectionHeader>
            <Collapse in={isExpanded}>{rows}</Collapse>
          </Box>
        )
      }

      return (
        <Box key={sectionIdx}>
          <SectionHeader sectionColor={section.color}>
            <Typography variant="subtitle2" fontWeight={700} color={section.color || 'text.primary'}>
              {section.title}
            </Typography>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              {t('tasks-count', { ns: 'maintenance', count: section.items.length })}
            </Typography>
          </SectionHeader>
          {rows}
        </Box>
      )
    })
  }

  if (isDialog) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <DialogHeader>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1, minWidth: 0 }}>
            <HeaderIconBox>
              <MaintenanceHeaderIcon width={22} height={22} />
            </HeaderIconBox>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" fontWeight={700} color={getThemeColor(theme, MUIThemeColors.black)}>
                {t('maintenance', { ns: 'maintenance' })}
              </Typography>
            </Box>
          </Box>
          {pop && (
            <IconButton aria-label="close" onClick={pop} size="small">
              <Close />
            </IconButton>
          )}
        </DialogHeader>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            flexWrap: 'wrap',
            mb: 1.5,
          }}
        >
          {renderTabs(true)}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            <Button
              variant="outlined"
              startIcon={<History />}
              onClick={handleHistoryClick}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderColor: theme.palette.divider,
                color: theme.palette.text.primary,
                whiteSpace: 'nowrap',
              }}
            >
              {`${t('history', { ns: 'common' })} ${historyCount}`}
            </Button>
          </Box>
        </Box>

        {renderSearchAndGroup(true)}

        {filteredItems.length > 0 && (
          <ModernSelectAllBar>
            <SelectAllBarLeft>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Checkbox
                  checked={allVisibleSelected}
                  indeterminate={selectedIds.size > 0 && !allVisibleSelected}
                  onChange={toggleSelectAll}
                  size="small"
                />
                <Typography variant="body2" color="text.primary" fontWeight={500}>
                  {t('select-all', { ns: 'common' })} ({filteredItems.length})
                </Typography>
              </Box>
              {selectedIds.size > 0 && (
                <CheckedCountText>
                  {t('checked-count', { ns: 'maintenance', count: selectedIds.size })}
                </CheckedCountText>
              )}
            </SelectAllBarLeft>
            {selectedIds.size > 0 && (
              <SelectAllBarActions>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  startIcon={isBatchPerforming ? <CircularProgress size={16} color="inherit" /> : <Check />}
                  onClick={handleBatchPerform}
                  disabled={isBatchPerforming || performMutation.isLoading}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    minWidth: 110,
                  }}
                >
                  {t('sign-selected', { ns: 'maintenance' })}
                </Button>
              </SelectAllBarActions>
            )}
          </ModernSelectAllBar>
        )}

        <ModernListContainer>{renderListContent()}</ModernListContainer>
      </Box>
    )
  }

  return (
    <CommonPageLayout
      titleSection={{ title: t('maintenance', { ns: 'maintenance' }) }}
      topAction={[
        <StyledPerformedButton
          key="history"
          variant="outlined"
          buttonText={t('history', { ns: 'common' })}
          onClick={handleHistoryClick}
        />,
        {
          buttonText: isTabletScreen ? '' : t('add-maintenance', { ns: 'maintenance' }),
          buttonIcon: <AddCircleOutline />,
          variant: 'outlined',
          shadowWeight: 0,
          height: '44px',
          buttonTextProps: { fontSize: '18px' },
          onClick: onAdd,
        },
      ]}
    >
      {renderTabs()}
      {renderSearchAndGroup()}
      {filteredItems.length > 0 && (
        <SelectAllBar>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Checkbox
              checked={allVisibleSelected}
              indeterminate={selectedIds.size > 0 && !allVisibleSelected}
              onChange={toggleSelectAll}
              size="small"
            />
            <Typography variant="body2" color="text.secondary">
              {t('select-all', { ns: 'common' })} ({filteredItems.length})
            </Typography>
          </Box>
          {selectedIds.size > 0 && (
            <Button
              size="small"
              variant="contained"
              color="primary"
              startIcon={isBatchPerforming ? <CircularProgress size={16} color="inherit" /> : <Check />}
              onClick={handleBatchPerform}
              disabled={isBatchPerforming || performMutation.isLoading}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {t('sign-selected', { ns: 'maintenance' })}
            </Button>
          )}
        </SelectAllBar>
      )}
      <ListContainer>{renderListContent()}</ListContainer>
      <BatchActionBar
        selectedCount={selectedIds.size}
        onPerformAll={handleBatchPerform}
        isPerforming={isBatchPerforming || performMutation.isLoading}
        isSigning={isBatchPerforming}
      />
    </CommonPageLayout>
  )
}

export default MaintenanceListView

const ListContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: 4,
  border: `1px solid ${theme.palette.divider}`,
  overflow: 'hidden',
  flex: 1,
  [theme.breakpoints.down('sm')]: {
    borderRadius: 0,
    borderLeft: 'none',
    borderRight: 'none',
    marginLeft: -8,
    marginRight: -8,
  },
}))

const SectionHeader = styled(Box)<{ sectionColor?: string }>(({ theme, sectionColor }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px 12px',
  backgroundColor: theme.palette.action.hover,
  borderBottom: `1px solid ${theme.palette.divider}`,
  borderLeft: `3px solid ${sectionColor || theme.palette.text.secondary}`,
  [theme.breakpoints.down('sm')]: {
    padding: '6px 10px',
  },
}))

const SubSectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '4px 12px 4px 24px',
  backgroundColor: theme.palette.action.selected,
  borderBottom: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.down('sm')]: {
    padding: '4px 10px 4px 18px',
  },
}))

const SelectAllBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '4px 12px',
  backgroundColor: theme.palette.action.hover,
  borderRadius: 4,
  marginBottom: 8,
  border: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.down('sm')]: {
    padding: '4px 8px',
  },
}))

const DialogHeader = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  marginBottom: 16,
})

const HeaderIconBox = styled(Box)(({ theme }) => ({
  width: 40,
  height: 40,
  borderRadius: 8,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  backgroundColor: getThemeColor(theme, MUIThemeColors.primaryDark),
}))

const ModernSelectAllBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '10px 14px',
  backgroundColor: theme.palette.background.paper,
  borderRadius: 8,
  marginBottom: 8,
  border: `1px solid ${theme.palette.divider}`,
  gap: 12,
  flexWrap: 'wrap',
}))

const SelectAllBarLeft = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  flex: 1,
  minWidth: 0,
  flexWrap: 'wrap',
})

const CheckedCountText = styled(Typography)(({ theme }) => ({
  fontSize: '0.7rem',
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: getThemeColor(theme, MUIThemeColors.primaryMain),
  whiteSpace: 'nowrap',
}))

const SelectAllBarActions = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexShrink: 0,
})

const ModernListContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: 8,
  border: `1px solid ${theme.palette.divider}`,
  overflow: 'hidden',
  flex: 1,
  maxHeight: '55vh',
  overflowY: 'auto',
}))

const CollapsibleSectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '10px 16px',
  backgroundColor: theme.palette.action.hover,
  borderBottom: `1px solid ${theme.palette.divider}`,
  cursor: 'pointer',
  userSelect: 'none',
  '&:hover': {
    backgroundColor: theme.palette.action.selected,
  },
}))
